/**
 * DAG pipeline factories for game orchestration.
 *
 * Each factory returns a DAGNode[] ready for DAGExecutor.run().
 * Pipelines express the dependency graph for a game phase
 * (turn, opening scene, character creation) and enable
 * parallel execution of independent steps.
 */

import type { DAGNode } from "./dag";

const TIMEOUT_PROCESS_ACTION_MS = 10_000;
const TIMEOUT_NARRATIVE_MS = 60_000;
const TIMEOUT_OPTIONS_MS = 30_000;
const TIMEOUT_FETCH_STATE_MS = 5_000;

const RETRIES_PROCESS_ACTION = 1;
const RETRIES_NARRATIVE = 3;
const RETRIES_OPTIONS = 3;

/**
 * Turn pipeline: process action -> generate narrative -> generate options.
 *
 * Options depend on the narrative so they can reference the actual scene,
 * NPCs, and locations from the generated text.
 */
export function turnPipeline(params: {
  action: string;
  processAction: (action: string) => Promise<string>;
  generateNarrative: (context: string) => Promise<string>;
  generateOptions: (narrative: string, context: string) => Promise<string>;
}): DAGNode[] {
  return [
    {
      id: "process-action",
      dependsOn: [],
      retries: RETRIES_PROCESS_ACTION,
      timeout: TIMEOUT_PROCESS_ACTION_MS,
      execute: async () => params.processAction(params.action),
    },
    {
      id: "narrative",
      dependsOn: ["process-action"],
      retries: RETRIES_NARRATIVE,
      timeout: TIMEOUT_NARRATIVE_MS,
      execute: async (inputs) => params.generateNarrative(inputs["process-action"]),
    },
    {
      id: "options",
      dependsOn: ["narrative", "process-action"],
      retries: RETRIES_OPTIONS,
      timeout: TIMEOUT_OPTIONS_MS,
      execute: async (inputs) =>
        params.generateOptions(inputs["narrative"], inputs["process-action"]),
    },
  ];
}

/**
 * Opening scene pipeline: fetch world state -> generate opening -> generate options.
 *
 * Options depend on the opening narrative so they can reference the actual scene,
 * NPCs, and locations from the generated text.
 */
export function openingScenePipeline(params: {
  fetchWorldState: () => Promise<string>;
  generateOpening: (context: string) => Promise<string>;
  generateOptions: (narrative: string, context: string) => Promise<string>;
}): DAGNode[] {
  return [
    {
      id: "fetch-state",
      dependsOn: [],
      retries: RETRIES_PROCESS_ACTION,
      timeout: TIMEOUT_FETCH_STATE_MS,
      execute: async () => params.fetchWorldState(),
    },
    {
      id: "opening",
      dependsOn: ["fetch-state"],
      retries: RETRIES_NARRATIVE,
      timeout: TIMEOUT_NARRATIVE_MS,
      execute: async (inputs) => params.generateOpening(inputs["fetch-state"]),
    },
    {
      id: "options",
      dependsOn: ["opening", "fetch-state"],
      retries: RETRIES_OPTIONS,
      timeout: TIMEOUT_OPTIONS_MS,
      execute: async (inputs) => params.generateOptions(inputs["opening"], inputs["fetch-state"]),
    },
  ];
}

/**
 * Character creation pipeline: generate backgrounds (single independent call).
 */
export function backgroundPipeline(params: {
  generateBackgrounds: () => Promise<string>;
}): DAGNode[] {
  return [
    {
      id: "backgrounds",
      dependsOn: [],
      retries: RETRIES_OPTIONS,
      timeout: TIMEOUT_OPTIONS_MS,
      execute: async () => params.generateBackgrounds(),
    },
  ];
}
